import { useMemo, useState } from 'react'
import { ESTADOS_PROPIEDAD, SOCIAS, type Rec } from '../../shared/schema'
import { useRecords } from '../lib/api'
import { useEditor } from '../lib/useOpen'
import { Link } from '../lib/router'
import { fmtDay, fmtDayLong, fmtTime, mapsLink, today, usd } from '../lib/format'
import { RecordForm } from '../components/RecordForm'
import { PrintPreview } from '../components/Print'
import { Avatar, Badge, Button, Empty, ErrorNote, Icon, Logo, PageHeader, Skeleton, Star } from '../components/ui'

export function Propiedades() {
  const { data, loading, error, refresh } = useRecords('propiedades')
  const clientes = useRecords('clientes').data
  const visitas = useRecords('visitas').data
  const evaluaciones = useRecords('evaluaciones').data
  const ed = useEditor(data)
  const [q, setQ] = useState('')
  const [estado, setEstado] = useState('')
  const [socia, setSocia] = useState('')
  const [ciudad, setCiudad] = useState('')
  const [min, setMin] = useState('')
  const [max, setMax] = useState('')
  const [dossier, setDossier] = useState<Rec | null>(null)

  const ciudades = useMemo(() => [...new Set(data.map(p => p.ciudad).filter(Boolean))].sort(), [data])
  const list = data.filter(p =>
    (!q || [p.nombre, p.direccion, p.ciudad, p.ref].join(' ').toLowerCase().includes(q.toLowerCase())) &&
    (!estado || p.estado === estado) && (!socia || p.socia === socia) && (!ciudad || p.ciudad === ciudad) &&
    (!min || (p.precio ?? 0) >= Number(min)) && (!max || (p.precio ?? Infinity) <= Number(max)))

  const counts = Object.fromEntries(ESTADOS_PROPIEDAD.map(e => [e, data.filter(p => p.estado === e).length]))

  return (
    <div className="page">
      <PageHeader eyebrow="Módulo 1" title="Propiedades" subtitle="Inventario y captaciones. Todo se guarda en DB_Propiedades."
        actions={<Button variant="primary" icon="plus" onClick={() => ed.create({ estado: 'Captación' })}>Nueva propiedad</Button>} />

      <div className="filters">
        <div className="search-input"><Icon name="search" size={16} /><input id="prop-q" type="search" placeholder="Buscar por nombre o dirección" value={q} onChange={e => setQ(e.target.value)} aria-label="Buscar propiedades" /></div>
        <div className="chips">
          <button type="button" className={`chip ${!estado ? 'on' : ''}`} onClick={() => setEstado('')}>Todas <span className="chip-n">{data.length}</span></button>
          {ESTADOS_PROPIEDAD.map(e => <button type="button" key={e} className={`chip ${estado === e ? 'on' : ''}`} onClick={() => setEstado(estado === e ? '' : e)}>{e} <span className="chip-n">{counts[e]}</span></button>)}
        </div>
        <div className="filter-row">
          <select id="prop-ciudad" value={ciudad} onChange={e => setCiudad(e.target.value)} aria-label="Ubicación"><option value="">Toda ubicación</option>{ciudades.map(c => <option key={c}>{c}</option>)}</select>
          <select id="prop-socia" value={socia} onChange={e => setSocia(e.target.value)} aria-label="Socia"><option value="">Ambas socias</option>{SOCIAS.map(s => <option key={s}>{s}</option>)}</select>
          <div className="range"><input id="prop-min" type="number" placeholder="USD mín." value={min} onChange={e => setMin(e.target.value)} aria-label="Precio mínimo" /><span>–</span><input id="prop-max" type="number" placeholder="USD máx." value={max} onChange={e => setMax(e.target.value)} aria-label="Precio máximo" /></div>
        </div>
      </div>

      {error && <ErrorNote error={error} onRetry={refresh} />}
      {loading ? <Skeleton rows={3} /> : list.length ? (
        <div className="prop-grid">
          {list.map(p => (
            <article key={p.id} className="prop-card" onClick={() => ed.open(p)} tabIndex={0} onKeyDown={e => e.key === 'Enter' && ed.open(p)}>
              <div className="prop-photo">
                {p.foto ? <img src={p.foto} alt={p.nombre} loading="lazy" /> : <PhotoPlaceholder name={p.nombre} />}
                <span className="prop-state"><Badge>{p.estado}</Badge></span>
              </div>
              <div className="prop-body">
                <div className="prop-top"><h3>{p.nombre}</h3><Avatar name={p.socia} /></div>
                <p className="prop-addr"><Icon name="map" size={14} />{[p.direccion, p.ciudad].filter(Boolean).join(', ') || 'Sin dirección'}</p>
                <div className="prop-specs">
                  {p.dormitorios != null && p.dormitorios !== '' && <span>{p.dormitorios} dorm.</span>}
                  {p.banos != null && p.banos !== '' && <span>{p.banos} baños</span>}
                  {p.m2 ? <span>{p.m2} m²</span> : p.terreno ? <span>{p.terreno} m² terreno</span> : null}
                  {p.orientacion && <span><Icon name="sun" size={13} /> {p.orientacion}</span>}
                </div>
                <div className="prop-foot"><strong className="price">{usd(p.precio)}</strong><span className="ref">{p.ref}</span></div>
              </div>
            </article>
          ))}
        </div>
      ) : <Empty title={data.length ? 'Ninguna propiedad coincide' : 'Todavía no hay propiedades'} text={data.length ? 'Probá con otros filtros.' : 'Cargá la primera captación para empezar el inventario.'} action={!data.length && <Button variant="primary" icon="plus" onClick={() => ed.create({ estado: 'Captación' })}>Nueva propiedad</Button>} />}

      <RecordForm col="propiedades" open={ed.isOpen} record={ed.editing} defaults={ed.creating ?? undefined} onClose={ed.close}
        top={() => ed.editing && <PropContext p={ed.editing} clientes={clientes} visitas={visitas} evaluaciones={evaluaciones} />}
        actions={rec => rec && <Button size="sm" variant="ghost" icon="printer" onClick={() => setDossier(rec)}>Dossier PDF</Button>} />

      <PrintPreview open={!!dossier} onClose={() => setDossier(null)} title={`Dossier · ${dossier?.nombre ?? ''}`}>
        {dossier && <Dossier p={dossier} evaluaciones={evaluaciones.filter(e => e.propiedad === dossier.id)} />}
      </PrintPreview>
    </div>
  )
}

export function PhotoPlaceholder({ name }: { name?: string }) {
  return (
    <div className="photo-ph">
      <Star size={28} color="var(--terracota)" />
      <span>{name}</span>
    </div>
  )
}

function PropContext({ p, clientes, visitas, evaluaciones }: { p: Rec; clientes: Rec[]; visitas: Rec[]; evaluaciones: Rec[] }) {
  const interesados = clientes.filter(c => (c.propiedadesInteres ?? []).includes(p.id))
  const vis = visitas.filter(v => v.propiedad === p.id).sort((a, b) => b.inicio.localeCompare(a.inicio))
  const evs = evaluaciones.filter(e => e.propiedad === p.id)
  const maps = mapsLink(p)
  return (
    <div className="context">
      {p.foto && <img className="context-hero" src={p.foto} alt="" />}
      <div className="context-grid">
        <div><span className="ctx-label">Precio</span><strong className="ctx-big">{usd(p.precio)}</strong></div>
        <div><span className="ctx-label">Interesados</span><strong className="ctx-big">{interesados.length}</strong></div>
        <div><span className="ctx-label">Visitas</span><strong className="ctx-big">{vis.length}</strong></div>
      </div>
      {maps && <a className="inline-link" href={maps} target="_blank" rel="noreferrer"><Icon name="map" size={14} /> Abrir en Google Maps</a>}
      {interesados.length > 0 && <div className="ctx-block"><span className="ctx-label">Clientes interesados</span><div className="chips">{interesados.map(c => <Link key={c.id} to={`/clientes?abrir=${c.id}`} className="chip">{c.nombre}</Link>)}</div></div>}
      {vis.length > 0 && <div className="ctx-block"><span className="ctx-label">Historial de visitas</span><ul className="ctx-list">{vis.slice(0, 4).map(v => <li key={v.id}><span>{fmtDay(v.inicio.slice(0, 10))} · {fmtTime(v.inicio)}</span> {v.titulo} <Badge>{v.estado}</Badge></li>)}</ul></div>}
      {evs.length > 0 && <div className="ctx-block"><span className="ctx-label">Evaluaciones guardadas</span><ul className="ctx-list">{evs.map(e => <li key={e.id}><span>{e.calculadora}</span> {e.resultado}</li>)}</ul></div>}
      <Link to={`/calculadoras?propiedad=${p.id}`} className="inline-link"><Icon name="calc" size={14} /> Evaluar con las calculadoras</Link>
    </div>
  )
}

function Dossier({ p, evaluaciones }: { p: Rec; evaluaciones: Rec[] }) {
  const photos = [p.foto, ...(p.galeria ?? [])].filter(v => v && /\.(jpe?g|png|webp|gif)(\?|$)|^data:image/i.test(String(v)))
  const confort = evaluaciones.find(e => e.calculadora === 'Confort biofílico')
  return (
    <article className="sheet dossier">
      <header className="sheet-head"><Logo size={26} /><span className="sheet-ref">{p.ref} · {fmtDayLong(today())}</span></header>
      <div className="dossier-cover">{photos[0] ? <img src={photos[0]} alt="" /> : <PhotoPlaceholder name={p.nombre} />}</div>
      <div className="dossier-title">
        <div><div className="eyebrow">{p.tipo || 'Propiedad'} · {p.ciudad}</div><h1>{p.nombre}</h1><p>{p.direccion}</p></div>
        <div className="dossier-price">{usd(p.precio)}</div>
      </div>
      <dl className="dossier-specs">
        {[['Dormitorios', p.dormitorios], ['Baños', p.banos], ['Superficie', p.m2 && `${p.m2} m²`], ['Terreno', p.terreno && `${p.terreno} m²`], ['Orientación', p.orientacion], ['Luz natural', p.iluminacion && `${p.iluminacion} / 5`], ['Ruido medido', p.ruido && `${p.ruido} dB`], ['Confort biofílico', confort?.resultado]]
          .filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => <div key={k as string}><dt>{k}</dt><dd>{v}</dd></div>)}
      </dl>
      {p.descripcion && <section className="dossier-desc"><h2>Lo que hay para descubrir</h2><p>{p.descripcion}</p></section>}
      {photos.length > 1 && <div className="dossier-gallery">{photos.slice(1, 7).map((src, i) => <img key={i} src={src} alt="" />)}</div>}
      <footer className="sheet-foot">
        <span>Te acompaña: <strong>{p.socia || 'REVELA'}</strong></span>
        <span>@revela.real.estate · Paysandú, Uruguay</span>
      </footer>
    </article>
  )
}
